<template>
  <section>
    <div class="mx-2 mt-3 mb-2 rounded-lg">
      <div class="flex mb-5 justify-center bg-white rounded-lg">
        <div
          class="px-2 pb-1 mt-3 -mb-3 mx-3 self-center text-center whitespace-normal text-2xl sm:text-4xl text-white bg-green-600/75  backdrop-blur-xs border border-white rounded-lg"
        >
          Personvernerklæring
        </div>
      </div>
    </div>
    <article class="prose mx-2 mt-3 px-3 pt-3 pb-12 bg-white rounded-lg">
      <p>Sist oppdatert: <time>2022-08-18</time></p>
      <h2 id="kort-fortalt">Kort fortalt</h2>
      <ul>
        <li>
          På denne siden vil vi fortelle hvilke opplysninger vi henter inn,
          hvordan vi behandler dem, og hvorfor.
        </li>
        <li>
          Vi lagrer <i>e-post adressen</i> din og/eller
          <i>mobiltelefonnummeret</i> ditt og et <i>brukernavn</i> hvis du
          oppretter en brukerkonto.
        </li>
        <li>
          Vi bruker <i>cookies/informasjonskapsler</i> til å lagre en
          midlertidig nøkkel mens du er innlogget, huske filtreringsregler for
          programoversikten og skjule cookie-banneret når du har godkjent bruken
          vår.
        </li>
        <li>
          Vi lagrer <i>tekst</i> og <i>bilder</i> du eller andre legger inn,
          inkludert eventuelle personopplysninger.
        </li>
        <li>
          Ta kontakt dersom du oppdager personopplysninger som ikke skulle vært
          tilgjengelige. Du kan kreve å få utlevert eller slettet alle
          personopplysninger vi har knyttet til deg.
        </li>
        <li>
          Bede.no kjører på
          <BedeLink to="https://firebase.google.com" :external="true">
            Firebase
          </BedeLink>
          /
          <BedeLink to="https://cloud.google.com/gcp" :external="true">
            Google Cloud Platform
          </BedeLink>
          infrastruktur. Derfor vil data du legger inn eller laster opp, og
          personopplysninger som innhentes indirekte (f.eks. IP-adressen din),
          bli behandlet og lagret hos Google.
          <BedeLink
            to="https://policies.google.com/technologies/partner-sites"
            :external="true"
          >
            Hvordan Google bruker informasjon fra nettsider eller app-er som
            benytter [Google sine] tjenester </BedeLink
          >.
        </li>
      </ul>
      <h2 id="hvem-vi-er">Hvem vi er</h2>
      <p>
        <BedeLink
          :to="{ path: '/om-oss', hash: '#kongsvinger-misjonsforsamling' }"
        >
          Kongsvinger Misjonsforsamling
        </BedeLink>
        (Tommelstadsgate 1, 2212 Kongsvinger / org.nr. 911980614) eier, driver
        og utvikler tjenesten
        <BedeLink to="https://bede.no">Bede.no</BedeLink>.
      </p>
      <p>
        Ta gjerne kontakt på e-post til
        <BedeLink to="mailto:bede.no@protonmail.com">
          Bede.no@ProtonMail.com
        </BedeLink>
        hvis du har spørsmål til denne personvernerklæringen eller har en
        forespørsel knyttet til personopplysningene dine.
      </p>
      <p>
        Styreleder i Kongsvinger Misjonsforsamling er behandlingsansvarlig for
        personopplysningene på Bede.no.
      </p>

      <h2 id="vi-respekterer-ditt-personopplysningsvern">
        Vi respekterer ditt personopplysningsvern
      </h2>
      <p>
        <!-- cspell: ignore ersonopplysninger -->
        <q
          >[P]ersonopplysninger» [er] enhver opplysning om en identifisert eller
          identifiserbar fysisk person («den registrerte»); en identifiserbar
          fysisk person er en person som direkte eller indirekte kan
          identifiseres, særlig ved hjelp av en identifikator, f.eks. et navn,
          et identifikasjonsnummer, lokaliseringsopplysninger, en
          nettidentifikator eller ett eller flere elementer som er spesifikke
          for nevnte fysiske persons fysiske, fysiologiske, genetiske, psykiske,
          økonomiske, kulturelle eller sosiale identitet</q
        >
        &mdash;
        <BedeLink
          to="https://lovdata.no/dokument/NL/lov/2018-06-15-38/gdpr/ARTIKKEL_4#gdpr/ARTIKKEL_4"
          :external="true"
        >
          GDPR Artikkel 4
        </BedeLink>
      </p>
      <p>
        Når du overlater personopplysninger og andre data til Bede.no, tar vi
        ansvaret på alvor. Vi beskytter dine personopplysninger og personlige
        data mot tilgang fra uvedkommende, begrenser tilgangen til behandlere
        med legitime tilgangsbehov og bruker dem kun til å gi deg og andre
        brukere av Bede.no en trygg og nyttig tjeneste.
      </p>

      <h2 id="innsamling-lagring-og-bruk-av-personopplysninger">
        Innsamling, lagring og bruk av personopplysninger
      </h2>

      <p>
        Noen opplysninger blir samlet inn ved at du selv legger dem inn på
        Bede.no, mens andre opplysninger blir indirekte samlet inn ved bruk av
        infrastrukturen som Bede.no kjører på.
      </p>

      <h3 id="opplysninger-du-selv-oppgir">Opplysninger du selv oppgir</h3>
      Hvis du ikke oppretter en brukerkonto på Bede.no, vil du ikke få mulighet
      til å eksplisitt oppgi opplysninger til oss. Dersom du derimot oppretter
      en brukerkonto og benytter deg av tjenester som kun er tilgjengelige for
      innloggede brukere, vil følgende opplysninger behandles av Bede.no.
      <ul>
        <li>
          <b>E-post adresse og telefonnummer</b>
          <p>
            Hvis du oppretter en brukerkonto på Bede.no, blir du bedt om å oppgi
            en <i>e-post adresse</i> og/eller et <i>mobiltelefonnummer</i>, slik
            at du kan resette passordet ditt hvis du glemmer det.
          </p>
          <p>
            Vi lagrer dette kun i
            <BedeLink
              to="https://firebase.google.com/products/auth/"
              :external="true"
            >
              Firebase Authentication </BedeLink
            >.
          </p>
        </li>
        <li>
          <b>Brukernavn</b>
          <p class="pt-0">
            Hvis du oppretter en brukerkonto på Bede.no, vil du bli bedt om å
            oppgi et unikt <i>brukernavn</i> som fungerer som din identitet
            overfor andre brukere på Bede.no og for det tekniske systemet.
            Bede.no benytter brukernavnet til å slå opp hvilke data du har
            tilgang til.
          </p>
          <p>
            Vi lagrer dette i
            <BedeLink
              to="https://firebase.google.com/products/auth/"
              :external="true"
            >
              Firebase Authentication
            </BedeLink>
            og i
            <BedeLink to="https://cloud.google.com/firestore/" :external="true">
              Cloud Firestore
            </BedeLink>
          </p>
        </li>
        <li>
          <b>Tekst og bilder</b>
          <p>
            Hvis du legger inn eller redigerer informasjon på Bede.no om f.eks.
            en lokasjon, en arrangør, en type aktivitet eller et enkelt
            arrangement, vil denne informasjonen knyttes til din brukerkonto.
            Andre brukere vil kunne se denne tilknytningen.
          </p>
          <p>
            Opplysninger du oppgir som kan brukes til å identifisere deg eller
            andre, f.eks. ut ifra kontekst eller basert på et bilde, er å regne
            som personopplysninger. Innlegging av slik informasjon reguleres av
            <BedeLink to="/juridisk/bruksvilkar">Bruksvilkårene</BedeLink> våre.
          </p>
          <p>
            Vi lagrer dette i
            <BedeLink to="https://cloud.google.com/firestore/" :external="true">
              Cloud Firestore
            </BedeLink>
            og i
            <BedeLink
              to="https://firebase.google.com/products/storage/"
              :external="true"
            >
              Cloud Storage for Firebase </BedeLink
            >.
          </p>
        </li>
        <li>
          <b>Tilknytning til lokasjon eller arrangør</b>
          <p>
            Hvis du melder interesse for å bli knyttet til en lokasjon eller
            arrangør slik at du kan få tilgang til informasjon forbeholdt
            brukere med slik tilknytning, vil din tilknytningsstatus til
            lokasjonen eller arrangøren være tilgjengelig for andre brukere.
          </p>
          <p>
            Vi lagrer dette i
            <BedeLink
              to="https://firebase.google.com/products/auth/"
              :external="true"
            >
              Firebase Authentication
            </BedeLink>
            og
            <BedeLink to="https://cloud.google.com/firestore/" :external="true">
              Cloud Firestore </BedeLink
            >.
          </p>
        </li>
      </ul>

      <h3 id="opplysninger-som-samles-inn-automatisk">
        Opplysninger som samles inn automatisk
      </h3>
      <p>
        Bede.no kjører på Firebase/Google Cloud Platform. Det betyr at din bruk
        av Bede.no ikke er mulig uten å kommunisere med Google sin
        infrastruktur. Dermed innhenter Google data om deg også når du ikke har
        opprettet en brukerkonto på Bede.no. Google har en egen side som
        beskriver
        <BedeLink
          to="https://policies.google.com/technologies/partner-sites"
          :external="true"
        >
          Hvordan Google bruker informasjon fra nettsider eller app-er som
          benytter [Google sine] tjenester </BedeLink
        >. Mer detaljert informasjon finner du i
        <BedeLink
          to="https://firebase.google.com/support/privacy"
          :external="true"
        >
          Personvern og sikkerhet i Firebase
        </BedeLink>
        og
        <BedeLink
          to="https://cloud.google.com/terms/cloud-privacy-notice"
          :external="true"
        >
          Google Cloud Personvernerklæring </BedeLink
        >.
      </p>

      <h4 id="firebase-google-tjenestene-vi-benytter">
        Firebase-/Google Cloud Platform-tjenestene vi benytter
      </h4>
      <ul>
        <li>
          <b>Firebase Hosting</b>
          <p>
            Bede.no gjøres tilgjengelig for nettlesere gjennom
            <BedeLink
              to="https://firebase.google.com/products/hosting/"
              :external="true"
            >
              Firebase Hosting </BedeLink
            >. Når du besøker Bede.no blir noe informasjon om bruken din
            automatisk samlet og lagret av Firebase Hosting sin infrastruktur,
            inkludert tidspunktet du besøker Bede.no, <i>IP-adressen</i> du
            besøker fra og
            <i>informasjon om nettleseren og operativsystemet</i> du bruker.
            Informasjonen lagres i noen måneder.
          </p>
        </li>
        <li>
          <b>Google Cloud Logging</b>
          <p>
            Informasjon om besøket ditt på Bede.no logges til tjenesten
            <BedeLink to="https://cloud.google.com/logging/" :external="true">
              Google Cloud Logging </BedeLink
            >, hvor besøksdata (inkludert
            <i
              >besøkstidspunkt, IP-adresse og nettleser- og
              operativsysteminformasjon</i
            >) lagres i inntil 30 dager.
          </p>
          <p>
            Google deler besøksdata med oss. Vi bruker denne informasjonen kun
            til å vurdere tilstanden til, og eventuelt feilsøke, Bede.no og
            infrastrukturen tjenesten kjører på, og eksporterer ikke slike data
            fra Google Cloud Logging.
          </p>
        </li>
        <li>
          <b>Firebase Authentication</b>
          <p>
            Dersom du oppretter en brukerkonto på Bede.no, lagres informasjon om
            brukerkontoen i
            <BedeLink
              to="https://firebase.google.com/products/auth/"
              :external="true"
            >
              Firebase Authentication </BedeLink
            >.
            <i
              >Passord, e-post adresse, telefonnummer, nettleserinformasjon og
              IP adresse</i
            >
            benyttes for å autentisere deg slik at du kan få tilgang til
            beskyttet informasjon og for å forhindre at uvedkommende får
            tilgang. Midlertidige tilgangsnøkler lagres i nettleseren din i en
            cookie/informasjonskapsel. <i>IP addresser</i> lagres i noen uker,
            mens <i>autentiseringsinformasjon</i> lagres inntil brukerkontoen
            slettes, hvorpå relaterte data slettes fra aktive system og
            sikkerhetskopier innen 180 dager.
          </p>
          <p>
            Google deler din <i>e-post adresse</i> og ditt
            <i>telefonnummer</i> med oss. Vi behandler denne informasjonen kun
            som en del av brukerkontoadministrasjonen gjennom Firebase
            Authentication og eksporterer ikke slike data fra Firebase
            Authentication.
          </p>
        </li>
        <li>
          <b>Firebase App Check</b>
          <p>
            <BedeLink
              to="https://firebase.google.com/products/app-check/"
              :external="true"
            >
              Firebase App Check
            </BedeLink>
            benytter
            <BedeLink
              to="https://www.google.com/recaptcha/about/"
              :external="true"
            >
              Google reCaptcha
            </BedeLink>
            til å verifisere at du og andre brukere er mennesker og forhindrer
            roboter og spam på Bede.no. App Check/reCaptcha lagrer et
            autentisitetsbevis i en cookie/informasjonskapsel i nettleseren din,
            og sender dette beviset med i alle forespørsler til alle
            Firebase/Google Cloud Platform tjenestene slik at tjenestene kan se
            at forespørselen er autentisk. Autentisitetsbeviset lagres ikke i
            Firebase/Google Cloud Platform.
          </p>
        </li>
        <li>
          <b>Cloud Functions for Firebase</b>
          <p>
            Bede.no bruker
            <BedeLink
              to="https://firebase.google.com/products/functions/"
              :external="true"
            >
              Cloud Functions for Firebase
            </BedeLink>
            til å kjøre kode på tjener-siden. Selv om data hentes fra de øvrige
            Firebase-/Google-tjenestene, blir data kun behandlet for
            videresending til nettleseren din. Cloud Functions for Firebase
            benytter også <i>IP adressen</i> din for å levere resultatet av
            databehandlingen til deg. Cloud Functions for Firebase oppbevarer
            slike data kun midlertidig.
          </p>
        </li>
        <li>
          <b>Cloud Firestore</b>
          <p>
            Informasjon om lokasjoner, arrangører, aktiviteter og arrangement
            lagres i
            <BedeLink to="https://cloud.google.com/firestore/" :external="true">
              Cloud Firestore </BedeLink
            >, sammen med tilknytninger mellom brukerkontoer, lokasjoner og
            arrangører.
          </p>
          <p>
            Vi har full tilgang til alle data lagret i Cloud Firestore, men
            behandler slike data kun programmatisk som en del av tjenesten
            Bede.no.
          </p>
        </li>
        <li>
          <b>Cloud Storage for Firebase</b>
          <p>
            Media som lastes opp til Bede.no lagres i
            <BedeLink
              to="https://firebase.google.com/products/storage/"
              :external="true"
            >
              Cloud Storage for Firebase
            </BedeLink>
            uten eksplisitte personopplysninger. Media kan likevel inneholde
            personopplysninger ved direkte identifikasjon i tekst, bilde eller
            andre media, eller indirekte gjennom sammenhold med annen
            informasjon.
          </p>
          <p>
            Vi har full tilgang til alle data lagret i Cloud Storage for
            Firebase, men behandler slike data kun programmatisk som en del av
            tjenesten Bede.no.
          </p>
        </li>
        <li>
          <b>Google Maps Platform</b>
          <p>
            Bede.no gjør tilgjengelig et kart fra Google for å gjøre det lettere
            å finne frem til adressen til lokasjoner som legges inn på Bede.no.
            Ifølge
            <BedeLink
              to="https://cloud.google.com/maps-platform/terms/"
              :external="true"
            >
              Google Maps Platform Terms
            </BedeLink>
            mottar Google data fra våre brukere via dette kartet i form av
            eventuelle søkeord, IP adresser eller din lokasjon. Bede.no henter
            ikke ut noe av denne informasjonen fra Google.
          </p>
        </li>
        <li>
          <b>Google Analytics</b>
          <p>
            Bede.no bruker &ndash; IKKE &ndash; Google Analytics. Vi vil
            oppdatere denne personvernerklæringen dersom det endrer seg.
          </p>
        </li>
        <!-- <li>
          <b>Firebase Dynamic Links</b>
          <p>
            Enhetsinformasjon lagres midlertidig og benyttes for å muliggjøre å åpne en link med en ny installasjon av BedeApp til linkens side.
          </p>
        </li> -->
        <!-- <li>
          <b>Firebase Cloud Messaging</b>
          <p>
            Instans-ID for å vite hvilken enhet en melding skal sendes til.
          </p>
        </li> -->
        <!-- <li>
          <b>Firebase Crash Reporting</b>
          <p>
            Stack-trace (programkall-historikk?) og instans-ID for diagnostisering.
          </p>
        </li> -->
        <!-- <li>
          <b>Firebase Crashlytics</b>
          <p>
            Brukerens installasjons-ID og IP adresse.
          </p>
        </li> -->
      </ul>

      <h3 id="cookies-informasjonskapsler-og-sporingsteknologier">
        Cookies/informasjonskapsler og sporingsteknologier
      </h3>

      <p>
        Cookies/informasjonskapsler er små tekstfiler som kan lagres i
        nettleseren din når du besøker en nettside og som ofte innholder en unik
        identifikator som potensielt kan knyttes til en identifiserbar person.
        Bede.no benytter kun informasjonskapsler til innlogging, for å luke bort
        roboter og spam og til andre funksjonelle formål som å huske
        filtreringen du har valgt for programoversikten.
      </p>

      <p>
        Bede.no benytter seg ikke av hverken informasjonskapsler eller andre
        teknologier for å spore deg på tvers av flere nettsider. Det er likevel
        mulig at Google kan spore deg gjennom indirekte innsamlet data.
      </p>

      <p>
        Mer om Bede.no sine
        <BedeLink to="/juridisk/informasjonskapsler">
          informasjonskapsler </BedeLink
        >.
      </p>

      <h3 id="personopplysninger-vi-ikke-innhenter">
        Personopplysninger vi ikke innhenter
      </h3>

      <ul>
        <li>
          Vi innhenter ikke opplysninger om noen under 13 år. Bede.no er ment
          for og tilrettelagt for brukere over 13 år.
        </li>
        <li>Vi samler ikke sensitive personopplysninger.</li>
        <li>Vi sporer deg ikke mellom nettsider.</li>
        <li>Vi bruker ikke analyseverktøy som Google Analytics.</li>
        <li>Vi bruker ikke markedsføringsverktøy eller reklame.</li>
      </ul>

      <h2 id="hvordan-vi-bruker-dine-opplysninger">
        Hvordan vi bruker dine opplysninger
      </h2>
      <p>
        Vi behandler informasjonen som innhentes via Bede.no utelukkende til å
        tilby Bede.no som en informasjonstjeneste til allmennheten og innloggede
        brukere.
      </p>
      <p>
        Vi selger ikke dine data og deler ingen data med andre aktører ut over
        det som automatisk deles med Firebase/Google Cloud Platform som en
        nødvendig del av tjenestens drift slik det beskrives i denne
        personvernerklæringen.
      </p>
      <p>
        Vi vil kun dele informasjon om deg med andre når vi mener loven krever
        det.
      </p>

      <h2 id="hvordan-vi-sikrer-personopplysningene-dine">
        Hvordan vi sikrer personopplysningene dine
      </h2>
      <p>
        Vi benytter etterprøvde systemer fra Firebase/Google og utvikler Bede.no
        i overensstemmelse med dokumentasjon fra Google.
      </p>
      <p>
        Vi begrenser tilgangen til Firebase/Google Cloud Platform, og dermed
        alle nevnte data som lagres der, til individer som har reelt og legitimt
        tilgangsbehov for å gjøre vedlikehold, feilsøking og videreutvikling.
      </p>
      <p>
        Vi behandler og lagrer ikke opplysninger om deg lenger enn nødvendig for
        det tiltenkte formålet og i henhold til inngåtte avtaler og ikke utover
        lovens rammer.
      </p>

      <h2 id="tilgang-til-dine-opplysninger">Tilgang til dine opplysninger</h2>
      <h4 id="du-har-rett-til">Du har rett til:</h4>
      <ul>
        <li>informasjon om innsamling og bruk av dine data</li>
        <li>en kopi av data vi har lagret tilknyttet deg</li>
        <li>retting av eller sletting av dine personlige data</li>
        <li>å kreve tilgang til dine data begrenset eller sperret</li>
        <li>å få overført dine data til en annen aktør</li>
        <li>å nekte behandling av dine personlige data</li>
        <li>
          å
          <BedeLink
            to="https://www.datatilsynet.no/om-datatilsynet/kontakt-oss/klage-til-datatilsynet/"
            :external="true"
            >klage til Datatilsynet</BedeLink
          >
          på vår behandling av dine personopplysninger
        </li>
      </ul>
    </article>
  </section>
</template>

<script setup lang="ts">
onMounted(() => window.scrollTo(0, 0));
</script>
